import { FunctionComponent, useEffect } from "react";

import { useCskPreviewMode } from "src/hooks/use-csk-preview-mode";
import { CSK_PREVIEW_QUERY, type CskPreviewMode } from "src/utils/csk-preview";

import CskPaywallCliffStandIn from "./CskPaywallCliffStandIn";
import * as styles from "./CskPopupPreview.css";

/**
 * Duet popups that set `document.body.style.overflow` (CSK-relevant):
 * - share / gift: `useScrollLock` on mobile
 * - regwall: `RegwallController.lockScroll`
 * - paywall cliff: Zephr `#zephr-zone-footer[data-scrollable="false"]` when
 *   `#zephr-anchor` intersects (injection site). Translate stays on the footer
 *   node, not `html`.
 */
const LOCK_BODY: ReadonlySet<CskPreviewMode> = new Set([
  "share",
  "gift",
  "regwall",
]);

function clickControl(selector: string): boolean {
  const control = document.querySelector<HTMLElement>(selector);
  if (!control) {
    return false;
  }
  control.click();
  return true;
}

function openRegwallPreview(): boolean {
  const container = document.querySelector("[data-regwall-container]");
  if (!container) {
    return false;
  }
  container.setAttribute("data-regwall-visible", "true");
  return true;
}

function retryUntil(
  attempt: () => boolean,
  intervalMs: number,
  timeoutMs: number,
): () => void {
  if (attempt()) {
    return () => undefined;
  }
  const intervalId = window.setInterval(() => {
    if (attempt()) {
      window.clearInterval(intervalId);
    }
  }, intervalMs);
  const timeoutId = window.setTimeout(() => {
    window.clearInterval(intervalId);
  }, timeoutMs);
  return () => {
    window.clearInterval(intervalId);
    window.clearTimeout(timeoutId);
  };
}

const CskPopupPreview: FunctionComponent = () => {
  const mode = useCskPreviewMode();
  const lockBody = Boolean(mode && LOCK_BODY.has(mode));

  useEffect(() => {
    if (!lockBody) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [lockBody]);

  useEffect(() => {
    if (mode === "share" || mode === "gift") {
      const selector =
        mode === "share"
          ? 'button[aria-label="Share article"]'
          : 'button[aria-label="Gift this article"]';
      return retryUntil(() => clickControl(selector), 250, 4000);
    }
    if (mode === "regwall") {
      return retryUntil(openRegwallPreview, 250, 4000);
    }
    return undefined;
  }, [mode]);

  if (!mode || mode === "suspense") {
    return null;
  }

  if (mode === "paywall") {
    return <CskPaywallCliffStandIn />;
  }

  return (
    <>
      {mode === "regwall" ? (
        <div
          className={styles.overlay}
          data-regwall-container=""
          data-regwall-visible="true"
          role="status"
        >
          CSK regwall stand-in
        </div>
      ) : null}
      <div className={styles.banner} role="status">
        CSK chrome preview:{" "}
        <code>
          ?{CSK_PREVIEW_QUERY}={mode}
        </code>
      </div>
    </>
  );
};

export default CskPopupPreview;
